import React, { useState, useCallback } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import './FlowchartEditor.css';

// Custom Diamond Node Component
// Build 28: exported so the read-only DiagramViewer reuses the exact
// same custom node rendering instead of duplicating it (DRY).
export const DiamondNode = ({ data, isConnectable }) => {
  return (
    <div className="diamond-node-wrapper">
      {/* Top handle - at the top point of diamond */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        style={{ top: 0, background: '#3b82f6' }}
      />
      {/* Right handle - at the right point of diamond */}
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{ right: 0, background: '#3b82f6' }}
      />
      {/* Bottom handle - at the bottom point of diamond */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        style={{ bottom: 0, background: '#3b82f6' }}
      />
      {/* Left handle - at the left point of diamond */}
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        style={{ left: 0, background: '#3b82f6' }}
      />
      <div className="diamond-node">
        <div className="diamond-content">{data.label}</div>
      </div>
    </div>
  );
};

export const nodeTypes = {
  diamond: DiamondNode
};

function FlowchartEditor({ diagram, onClose, onSave }) {
  const initialNodes = diagram.content?.nodes || [];
  const initialEdges = (diagram.content?.edges || []).map(edge => {
    const loadedEdge = {
      ...edge,
      style: edge.style || { stroke: '#3b82f6', strokeWidth: 2 }
    };
    
    // Restore arrow markers if they were saved
    if (edge.markerStart && edge.markerStart.type) {
      loadedEdge.markerStart = { type: MarkerType.ArrowClosed };
    }
    if (edge.markerEnd && edge.markerEnd.type) {
      loadedEdge.markerEnd = { type: MarkerType.ArrowClosed };
    }
    
    return loadedEdge;
  });

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeIdCounter, setNodeIdCounter] = useState(initialNodes.length + 1);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [editingEdge, setEditingEdge] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({
      ...params,
      style: { stroke: '#3b82f6', strokeWidth: 2 }
    }, eds)),
    [setEdges]
  );

  // Handle edge click to edit arrows
  const onEdgeClick = useCallback((event, edge) => {
    setEditingEdge(edge);
  }, []);

  // Handle arrow configuration
  const handleArrowConfig = (config) => {
    if (!editingEdge) return;

    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === editingEdge.id) {
          // Create new edge object without markerStart/markerEnd first
          const updatedEdge = {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            type: edge.type,
            style: edge.style
          };
          
          // Configure arrows based on selection
          if (config === 'none') {
            // No markers - leave them undefined
          } else if (config === 'end') {
            updatedEdge.markerEnd = { type: MarkerType.ArrowClosed };
          } else if (config === 'start') {
            updatedEdge.markerStart = { type: MarkerType.ArrowClosed };
          } else if (config === 'both') {
            updatedEdge.markerStart = { type: MarkerType.ArrowClosed };
            updatedEdge.markerEnd = { type: MarkerType.ArrowClosed };
          }
          
          return updatedEdge;
        }
        return edge;
      })
    );
    
    setEditingEdge(null);
  };

  // Handle node double-click to edit
  const onNodeDoubleClick = useCallback((event, node) => {
    setEditingNodeId(node.id);
    setEditingText(node.data.label);
  }, []);

  // Handle text editing
  const handleTextChange = (e) => {
    setEditingText(e.target.value);
  };

  const handleTextSubmit = () => {
    if (editingNodeId) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === editingNodeId
            ? { ...node, data: { ...node.data, label: editingText } }
            : node
        )
      );
      setEditingNodeId(null);
      setEditingText('');
    }
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTextSubmit();
    } else if (e.key === 'Escape') {
      setEditingNodeId(null);
      setEditingText('');
    }
  };

  // Handle delete key for nodes and edges
  const onNodesDelete = useCallback((deleted) => {
    console.log('Nodes deleted:', deleted);
  }, []);

  const onEdgesDelete = useCallback((deleted) => {
    console.log('Edges deleted:', deleted);
  }, []);

  const addNode = (type) => {
    const newNode = {
      id: `node_${nodeIdCounter}`,
      type: type,
      position: { x: 250, y: 100 + (nodeIdCounter * 20) },
      data: { label: `Node ${nodeIdCounter}` },
      style: getNodeStyle(type)
    };
    
    setNodes((nds) => nds.concat(newNode));
    setNodeIdCounter((id) => id + 1);
  };

  const getNodeStyle = (type) => {
    if (type === 'diamond') {
      // Diamond nodes use custom component, minimal style needed
      return {
        background: 'transparent',
        border: 'none',
        padding: 0
      };
    }

    const baseStyle = {
      padding: 10,
      border: '2px solid #3b82f6',
      borderRadius: type === 'circle' ? '50%' : '0.375rem',
      background: 'white',
      fontSize: '14px',
      minWidth: type === 'circle' ? 100 : 150,
      minHeight: type === 'circle' ? 100 : 60,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    return baseStyle;
  };

  const handleSave = () => {
    // Clean nodes and edges for Firestore (remove undefined values)
    const cleanNodes = nodes.map(node => ({
      id: node.id,
      type: node.type || 'default',
      position: node.position,
      data: node.data,
      style: node.style || {}
    }));

    const cleanEdges = edges.map(edge => {
      const cleanEdge = {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
        type: edge.type || 'default',
        style: {
          stroke: '#3b82f6',
          strokeWidth: 2
        }
      };

      // Preserve arrow configuration
      if (edge.markerStart) {
        cleanEdge.markerStart = { type: 'arrowclosed' };
      }
      if (edge.markerEnd) {
        cleanEdge.markerEnd = { type: 'arrowclosed' };
      }

      return cleanEdge;
    });

    const content = {
      nodes: cleanNodes,
      edges: cleanEdges
    };
    
    onSave(content);
    alert('Diagram saved!');
  };

  const handleExport = (format) => {
    // Simple export - in production would use proper export library
    alert(`Export to ${format} - Feature coming soon!`);
  };

  return (
    <div className="flowchart-editor">
      <header className="editor-header">
        <div className="editor-title">
          <h2>📊 {diagram.title}</h2>
          <span className="editor-mode-badge">Flowchart Mode</span>
        </div>
        <div className="editor-actions">
          <button className="btn btn-secondary btn-small" onClick={handleSave}>
            💾 Save
          </button>
          <button className="btn btn-secondary btn-small" onClick={() => handleExport('PNG')}>
            📥 Export
          </button>
          <button className="btn btn-secondary btn-small" onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </header>

      <div className="editor-container">
        <aside className="editor-toolbar">
          <h3>Add Shapes</h3>
          <button className="toolbar-btn" onClick={() => addNode('default')}>
            <span>▭</span>
            Rectangle
          </button>
          <button className="toolbar-btn" onClick={() => addNode('circle')}>
            <span>●</span>
            Circle
          </button>
          <button className="toolbar-btn" onClick={() => addNode('diamond')}>
            <span>◆</span>
            Diamond
          </button>
          
          <div className="toolbar-divider"></div>
          
          <h3>Actions</h3>
          <button 
            className="toolbar-btn toolbar-btn-danger" 
            onClick={() => {
              const selectedNodes = nodes.filter(n => n.selected);
              const selectedEdges = edges.filter(e => e.selected);
              if (selectedNodes.length > 0 || selectedEdges.length > 0) {
                setNodes((nds) => nds.filter(n => !n.selected));
                setEdges((eds) => eds.filter(e => !e.selected));
              } else {
                alert('Select a shape or line first, then click delete');
              }
            }}
          >
            <span>🗑️</span>
            Delete Selected
          </button>
          
          <div className="toolbar-divider"></div>
          
          <div className="toolbar-instructions">
            <h4>Instructions:</h4>
            <ul>
              <li>Click shapes above to add</li>
              <li>Drag shapes to reposition</li>
              <li>Click & drag from edge to connect</li>
              <li>Double-click node to edit text</li>
              <li>Click line to configure arrows</li>
              <li>Click to select, then delete</li>
            </ul>
          </div>
        </aside>

        <div className="editor-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeClick={onEdgeClick}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            nodeTypes={nodeTypes}
            deleteKeyCode="Delete"
            defaultEdgeOptions={{
              type: 'default',
              style: { stroke: '#3b82f6', strokeWidth: 2 }
            }}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>

          {/* Text Editing Modal */}
          {editingNodeId && (
            <div className="text-editor-overlay">
              <div className="text-editor-modal">
                <h3>Edit Node Text</h3>
                <input
                  type="text"
                  value={editingText}
                  onChange={handleTextChange}
                  onKeyDown={handleTextKeyDown}
                  autoFocus
                  placeholder="Enter text..."
                />
                <div className="text-editor-buttons">
                  <button className="btn btn-primary" onClick={handleTextSubmit}>
                    Save
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setEditingNodeId(null);
                      setEditingText('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <small>Press Enter to save, Esc to cancel</small>
              </div>
            </div>
          )}

          {/* Arrow Configuration Modal */}
          {editingEdge && (
            <div className="text-editor-overlay">
              <div className="text-editor-modal">
                <h3>Configure Arrow</h3>
                <p style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                  Choose arrow placement for this connection
                </p>
                <div className="arrow-options">
                  <button 
                    className="arrow-option-btn"
                    onClick={() => handleArrowConfig('none')}
                  >
                    <div className="arrow-preview">━━━━</div>
                    <span>No Arrows</span>
                  </button>
                  <button 
                    className="arrow-option-btn"
                    onClick={() => handleArrowConfig('end')}
                  >
                    <div className="arrow-preview">━━━▶</div>
                    <span>Arrow at End</span>
                  </button>
                  <button 
                    className="arrow-option-btn"
                    onClick={() => handleArrowConfig('start')}
                  >
                    <div className="arrow-preview">◀━━━</div>
                    <span>Arrow at Start</span>
                  </button>
                  <button 
                    className="arrow-option-btn"
                    onClick={() => handleArrowConfig('both')}
                  >
                    <div className="arrow-preview">◀━━▶</div>
                    <span>Both Ends</span>
                  </button>
                </div>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setEditingEdge(null)}
                  style={{ marginTop: '1rem', width: '100%' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FlowchartEditor;
